// react, ability, auth and services
import { useAbility } from "@casl/react";
import { AbilityContext } from "Can";
import { AuthContext } from "context";
import { useContext, useEffect, useState } from "react";
import {useNavigate } from "react-router-dom";
import crudsService from "services/cruds-service";
import InventoryService from "services/inventory-items-service";

// Material Dashboard and MUI components
import ClearTwoToneIcon from '@mui/icons-material/ClearTwoTone';
import DoneAllTwoToneIcon from "@mui/icons-material/DoneAllTwoTone";
import VisibilityTwoToneIcon from "@mui/icons-material/VisibilityTwoTone";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import Snackbar from "@mui/material/Snackbar";
import Tooltip from "@mui/material/Tooltip";
import MDAvatar from "components/MDAvatar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Grid from "@mui/material/Grid";

//E4A Components
import ConfirmDialog from "e4acomponents/ConfirmDialog";
import Footer from "e4acomponents/Footer";
import DashboardLayout from "e4acomponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "e4acomponents/Navbars/DashboardNavbar";
import DataTable from "e4acomponents/Tables/DataTable";
import MiniStatisticsCard from "e4acomponents/Cards/StatisticsCards/MiniStatisticsCard";
import EditReservationDialog from "./edit-reservation-dialog";

export default function ViewReservations() {
    const [reservationsData, setReservationsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tableData, setTableData] = useState([]);
    const [avatars, setAvatars] = useState({});
    const [currentUser, setCurrentUser] = useState(null);
    const [statistics, setStatistics] = useState({});
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [reservationId, setReservationId] = useState(null);

    // Snakcbar states
    const [snackbarProps, setSnackbarProps] = useState({ open: false, message: "" });

    // Confirmation Dialog states
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [dialogProps, setDialogProps] = useState({});

    // Confirmation Dialog texts
    const fulfillPrompt = "Are you sure you want to fulfill this reservation?";
    const fulfillTitle = "Fulfill Reservation";
    const cancelPrompt = "Are you sure you want to cancel this reservation?";
    const cancelTitle = "Cancel Reservation";

    const ability = useAbility(AbilityContext);
    const authContext = useContext(AuthContext);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchReservations = async () => {
            try {
                setLoading(true);
                const response = await InventoryService.getAllReservations();
                const reservations = response.data;

                const images = await fetchImages(reservations);
                setAvatars(images);
                setReservationsData(reservations);

                const user = await authContext.getCurrentUser();
                setCurrentUser(user);

            } catch (error) {
                console.error("Error fetching reservations:", error);
            } finally {
                setLoading(false);
            }
        }
        fetchReservations();
    }, []);

    useEffect(() => {
        if (reservationsData && reservationsData.length > 0) {
            const rows = getRows(reservationsData);
            setTableData(rows);

            // Calculate Statistics
            const open = reservationsData.filter((r) => r.status === "allocated").length;
            const closed = reservationsData.filter((r) => r.status === "fulfilled").length;
            const cancelled = reservationsData.filter((r) => r.status === "cancelled").length;
            const qty_rsrvd = reservationsData
                .filter((r) => r.status === "allocated")
                .reduce((acc, curr) => acc + (parseInt(curr.qty) || 0), 0);

            setStatistics({ open, closed, cancelled, qty_rsrvd });
        }

    }, [reservationsData]);

    // Fetch user profile images for reservations
    const fetchImages = async (reservations) => {
        let images = {};
        for (let i = 0; i < reservations.length; i++) {
            const rsrv = reservations[i];
            if (!images[rsrv.user._id] && !avatars[rsrv.user._id]) {
                try {
                    images[rsrv.user._id] = await crudsService.getImage(rsrv.user.profile_image);
                } catch (err) {
                    images[rsrv.user._id] = null;
                }
            }
        }
        return images;
    }

    // Status styles
    const statusStyles = {
        allocated: { color: "info", text: "Allocated" },
        fulfilled: { color: "success", text: "Fulfilled" },
        cancelled: { color: "error", text: "Cancelled" },
    }

    // Prepare table rows from reservations data
    const getRows = (info) => {
        return info.map((reservation) => ({
            id: reservation._id,
            qty: reservation.qty,
            createdAt: new Date(reservation.created_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: '2-digit'
            }),
            user: reservation.user?.name || 'Unknown',
            userId: reservation.user?._id || null,
            avatar: avatars[reservation.user?._id] || null,
            status: reservation.status,
            comment: reservation.comment || '',
            inventoryItemId: reservation.inventoryItem || null,
            statusChip: (
                <Chip variant="outlined" size="small"
                    color={statusStyles[reservation.status]?.color || "default"}
                    label={statusStyles[reservation.status]?.text || reservation.status}
                />
            )
        }))
    }

    const dataTableData = {
        columns: [
            {
                Header: "Avatar", accessor: "avatar", disableSortBy: true,
                Cell: ({ cell: { value } }) => {
                    return (
                        <MDBox
                            sx={{ cursor: "pointer" }}
                        >
                            <MDAvatar src={value} alt="profile-image" size="xs" shadow="sm" />
                        </MDBox >
                    )
                }
            },
            { Header: 'User', accessor: 'user' },
            { Header: 'Created At', accessor: 'createdAt' },
            { Header: 'Comment', accessor: 'comment' },
            { Header: 'Quantity', accessor: 'qty', type: 'number' },
            {
                Header: 'Status',
                accessor: 'status',
                Cell: ({ row }) => row.original.statusChip,
                type: "singleSelect",
                valueOptions: ["allocated", "fulfilled", "cancelled"]
            },
            {
                Header: 'Actions', accessor: 'actions', disableSortBy: true,
                Cell: ({ row }) => {
                    return (
                        ability.can("manage", "reservation") ||
                        currentUser === row.original.userId
                    ) && (row.original.status === 'allocated') &&
                        (
                            <MDBox>
                                <Tooltip
                                    title="Fulfill Reservation"
                                    onClick={() => handleOpenConfirmDialog(row.original.id, 'fulfilled')}
                                >
                                    <IconButton>
                                        <DoneAllTwoToneIcon color="success" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip
                                    title="Cancel Reservation"
                                    onClick={() => handleOpenConfirmDialog(row.original.id, 'cancelled')}
                                >
                                    <IconButton>
                                        <ClearTwoToneIcon color="error" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip
                                    title="Edit Reservation"
                                    onClick={() => handleOpenEditDialog(row.original.id)}
                                >
                                    <IconButton>
                                        <EditTwoToneIcon color="success" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip
                                    title="View Item"
                                    onClick={() => navigate(`/inventory-management/view-specific-item/${row.original.inventoryItemId}`)}
                                >
                                    <IconButton>
                                        <VisibilityTwoToneIcon color="info" />
                                    </IconButton>
                                </Tooltip>
                            </MDBox>

                        )
                }
            }
        ],
        rows: tableData
    }

    // Update local state to reflect status change
    const reflectStatusChange = (reservationId, newStatus) => {
        const updatedReservations = reservationsData.map((reservation) =>
            reservation._id === reservationId
                ? { ...reservation, status: newStatus }
                : reservation
        );
        setReservationsData(updatedReservations);
    }

    // Update local state to reflect edited reservation
    const reflectEditedReservation = (reservationId, updatedData) => {
        const updatedReservations = reservationsData.map((reservation) =>
            reservation._id === reservationId
                ? { ...reservation, ...updatedData }
                : reservation
        );
        setReservationsData(updatedReservations);
    }

    // Handle Confirmation Dialog close
    const onCloseConfirmDialog = (response, title, reservationId) => {
        setOpenConfirmDialog(false);
        if (response) {
            if (title === fulfillTitle) {
                handleStatusChange(reservationId, 'fulfilled');
            } else if (title === cancelTitle) {
                handleStatusChange(reservationId, 'cancelled');
            }
        }
    }

    // Handles opening Confirmation Dialog
    const handleOpenConfirmDialog = (reservationId, status) => {
        if (status === 'fulfilled') {
            setDialogProps({
                title: fulfillTitle,
                prompt: fulfillPrompt,
                id: reservationId,
                onClose: onCloseConfirmDialog
            })
        } else if (status === 'cancelled') {
            setDialogProps({
                title: cancelTitle,
                prompt: cancelPrompt,
                id: reservationId,
                onClose: onCloseConfirmDialog
            })
        }
        setOpenConfirmDialog(true);
    }

    // Handle Status Change (Fulfill/Cancel)
    const handleStatusChange = async (reservationId, status) => {
        try {
            const payload = {
                data: {
                    attributes: {
                        status: status
                    }
                }
            };

            await InventoryService.editReservationStatus(reservationId, payload);
            reflectStatusChange(reservationId, status);

            // Feedback to user
            setSnackbarProps({
                open: true,
                message: status === 'fulfilled' ? 'Reservation fulfilled successfully.' : 'Reservation cancelled successfully.',
            })

        } catch (error) {
            console.error("Error fulfilling reservation:", error);
        }
    }

    // Handle Open Edit Dialog
    const handleOpenEditDialog = (reservationId) => {
        setReservationId(reservationId);
        setOpenEditDialog(true);
    }

    // Handle Close Edit Dialog
    const handleCloseEditDialog = (qty,comment) => {
        setReservationId(null);
        setOpenEditDialog(false);
        if (qty !== undefined && comment !== undefined) {
            reflectEditedReservation(reservationId, { qty, comment });
        }
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />

            <ConfirmDialog
                open={openConfirmDialog}
                props={dialogProps}
            />

            <Snackbar
                open={snackbarProps.open}
                autoHideDuration={3000}
                onClose={() => setSnackbarProps({ open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbarProps({ open: false })}
                    severity="success"
                    sx={{ width: '100%', alignItems: 'center' }}
                >
                    {snackbarProps.message}
                </Alert>
            </Snackbar>

            <EditReservationDialog
                open={openEditDialog}
                reservationId={reservationId}
                handleClose={handleCloseEditDialog}
            />

            <MDBox pt={3} >
                <Grid container spacing={3}>

                    {/* Reservation Statistics Cards */}
                    <Grid item xs={12} md={6} lg={3}>
                        <MiniStatisticsCard
                            title={{ text: "Quantity Currently Reserved" }}
                            count={statistics.qty_rsrvd || 0}
                            icon={{ color: "secondary", component: "inventory_2" }}
                        />
                    </Grid>

                    {/* Reservation Status Stats */}
                    <Grid item xs={12} md={6} lg={3}>
                        <MiniStatisticsCard
                            title={{ text: "Open Reservations" }}
                            count={statistics.open || 0}
                            icon={{ color: "info", component: "hourglass_top" }}
                        />
                    </Grid>

                    {/* Reservation Status Stats */}
                    <Grid item xs={12} md={6} lg={3}>
                        <MiniStatisticsCard
                            title={{ text: "Closed Reservations" }}
                            count={statistics.closed || 0}
                            icon={{ color: "success", component: "check_circle" }}
                        />
                    </Grid>

                    {/* Reservation Status Stats */}
                    <Grid item xs={12} md={6} lg={3}>
                        <MiniStatisticsCard
                            title={{ text: "Cancelled Reservations" }}
                            count={statistics.cancelled || 0}
                            icon={{ color: "error", component: "cancel" }}
                        />
                    </Grid>

                </Grid>
            </MDBox>

            <MDBox pt={3} pb={3}>

                <Card>

                    <MDBox p={3} lineHeight={1} display="flex" justifyContent="space-between">

                        {/* Title */}
                        <MDTypography variant="h5" fontWeight="medium">
                            Reservations
                        </MDTypography>

                    </MDBox>

                    {/* DataTable */}
                    <MDBox>
                        {loading ? (
                            <MDBox py={4} textAlign="center">
                                Loading reservations...
                            </MDBox>
                        ) : tableData.length === 0 ? (
                            <MDBox py={4} textAlign="center">
                                No reservations found.
                            </MDBox>
                        ) : (
                            <DataTable
                                table={dataTableData}
                                entriesPerPage={false}
                                showTotalEntries={false}
                                noEndBorder
                                canSearch
                            />
                        )}
                    </MDBox>
                </Card>
            </MDBox>
            <MDBox>
                <Footer />
            </MDBox>
        </DashboardLayout>
    )
}



